<template>
<!-- eslint-disable max-len -->
<div
  :class="{ 'decision-free-drag-root': true, native: (native), sf: (starfighter) }"
  ref="dragRoot"
>
  <div
    v-if="!(decision.hideTitle) || decision.description"
    class="decision-free-drag-text"
  >
    <h1
      v-if="!(decision.hideTitle)"
      class="decision-free-drag-title"
      v-html="decision.displayTitle || decision.title"
    ></h1>
    <div
      v-if="decision.description"
      class="decision-free-drag-description"
      v-html="decision.description"
    >
    </div>
  </div>
  <!-- DraggableImage per choice -->
  <draggable-image
    v-for="(choice, ch) in decision.choices"
    :key="`ch${ch}`"
    :settings="choice"
    :scale-factor="scaleFactor"
    :error-outline="
      (decision.overlapItemDropDisplayWarning || decision.overlapItemDropCancel)
      && (choice === draggedChoice ? (reactiveOverlapMap.size > 0) : reactiveOverlapMap.has(choice))
    "
    @drag-start="onDragChoiceStart"
    @drag-move="onDragChoiceMove"
    @drag-end="onDragChoiceEnd"
    @loaded-img="(ev) => onLoadedImg(ev, choice)"
  >
  </draggable-image>
  <!-- ResizeableBox per category -->
  <resizeable-box
    v-for="(category, c) in decision.categories"
    :key="`cat${c}`"
    :settings="category"
    :editing="editing"
    :hovered="category === hoveredCategory"
    :scale-factor="scaleFactor"
  >
  </resizeable-box>
  <!-- {{ choices }} -->
  <button
    class="btn decision-free-drag-submit"
    @click.prevent="submitDecision"
    :tabindex="0"
  >
    <div v-html="decision.choiceSelectButtonText ? decision.choiceSelectButtonText : 'Submit'"></div>
  </button>
</div>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapActions, mapMutations } from 'vuex';

import DraggableImage from './DraggableImage';
import ResizeableBox from './ResizeableBox';

import isInIframe from '../../../utils/iframe';

export default {
  props: {
    editing: { type: Boolean },
    decision: { type: Object },
  },
  components: {
    DraggableImage,
    ResizeableBox,
  },
  data() {
    return {
      choices: [],
      draggedChoice: null,
      hoveredCategory: null,
      startXPos: 0,
      startYPos: 0,
      scaleFactor: 1,
      aspectMap: new Map(),
      draggedOverlapMap: new Map(),
      draggedOverlapMapReactivityFlag: false,
    };
  },
  computed: {
    ...mapGetters([
      'roles',
      'allPlayers',
      'cash',
      'playerDecision',
      'playerGroupRole',
      'game',
      'disableForcedLandscape',
      'starfighter',
      'native'
    ]),
    reactiveOverlapMap() {
      return this.draggedOverlapMapReactivityFlag ? this.draggedOverlapMap : this.draggedOverlapMap;
    }
  },
  methods: {
    ...mapMutations([
      'setDisableForcedLandscape',
    ]),
    ...mapActions([
      'playSound',
      'playVoice',
      'stopSounds',
    ]),
    async playClick() {
      await Vue.nextTick();
      this.playSound({
        sound:
          this.decision.choiceSelectButtonSound
          || this.game.clickSoundOverride
          || this.resolveAssetPath('click_cut.mp3', 'ui/customer'),
        soundIsUnstoppable: true,
      });
    },
    async playChoiceInput() {
      const sound = this.decision.choiceInputChangeSound;
      if (sound) {
        await Vue.nextTick();
        this.playSound({
          sound,
          soundIsUnstoppable: true,
        });
      }
    },
    async submitDecision() {
      await this.playClick();
      await new Promise(resolve => setTimeout(resolve, 250));
      const { choices } = this;
      if (this.editing) {
        console.log(choices);
      }
      this.$emit('input', { choices });
    },
    computeCategoryForChoice(choice) {
      const aspect = (this.aspectMap.has(choice) ? this.aspectMap.get(choice) : (9 / 16));
      const choiceExtentX = +choice.xPos + (+choice.scale);
      const choiceExtentY = +choice.yPos + (+choice.scale * +aspect);
      for (let i = 0; i < this.decision.categories.length; ++i) {
        const category = this.decision.categories[i];
        if ((choice.xPos >= category.xPos) && (choice.xPos <= (category.xPos + category.width))
          && (choice.yPos >= category.yPos) && (choice.yPos <= (category.yPos + category.height))
          && (choiceExtentX >= category.xPos) && (choiceExtentX <= (category.xPos + category.width))
          && (choiceExtentY >= category.yPos) && (choiceExtentY <= (category.yPos + category.height))) {
          return category;
        }
      }
      return null;
    },
    computeItemOverlap(choice) {
      this.draggedOverlapMapReactivityFlag = !(this.draggedOverlapMapReactivityFlag);
      this.draggedOverlapMap.clear();
      if (this.decision.overlapItemDropDisplayWarning
        || this.decision.overlapItemDropCancel
        || this.decision.overlapItemDropAttemptCorrection) {
        const aspect = (this.aspectMap.has(choice) ? this.aspectMap.get(choice) : (9 / 16));
        const choiceExtentX = +choice.xPos + (+choice.scale);
        const choiceExtentY = +choice.yPos + (+choice.scale * +aspect);
        for (let i = 0; i < this.decision.choices.length; ++i) {
          const other = this.decision.choices[i];
          // eslint-disable-next-line no-continue
          if (choice === other) continue;

          const otherAspect = (this.aspectMap.has(other) ? this.aspectMap.get(other) : (9 / 16));
          const otherExtentX = +other.xPos + (+other.scale);
          const otherExtentY = +other.yPos + (+other.scale * +otherAspect);

          let xCorrection = 0;
          if ((+choice.xPos <= +other.xPos && otherExtentX <= choiceExtentX)
            || (+other.xPos <= +choice.xPos && choiceExtentX <= otherExtentX)) {
            // one fully contains other
            const leftCorrection = choiceExtentX - +other.xPos;
            const rightCorrection = (otherExtentX - +choice.xPos);
            xCorrection = (rightCorrection < leftCorrection) ? (-rightCorrection) : leftCorrection;
          } else if (+choice.xPos <= +other.xPos && +other.xPos < choiceExtentX && choiceExtentX < otherExtentX) {
            // left side of other overlaps
            xCorrection = choiceExtentX - +other.xPos;
          } else if (+other.xPos <= +choice.xPos && +choice.xPos < otherExtentX && otherExtentX < choiceExtentX) {
            // right side of other overlaps
            xCorrection = -(otherExtentX - +choice.xPos);
          }

          let yCorrection = 0;
          if ((+choice.yPos <= +other.yPos && otherExtentY <= choiceExtentY)
            || (+other.yPos <= +choice.yPos && choiceExtentY <= otherExtentY)) {
            // one fully contains other
            const leftCorrection = choiceExtentY - +other.yPos;
            const rightCorrection = (otherExtentY - +choice.yPos);
            yCorrection = (rightCorrection < leftCorrection) ? (-rightCorrection) : leftCorrection;
          } else if (+choice.yPos <= +other.yPos && +other.yPos < choiceExtentY && choiceExtentY < otherExtentY) {
            // left side of other overlaps
            yCorrection = choiceExtentY - +other.yPos;
          } else if (+other.yPos <= +choice.yPos && +choice.yPos < otherExtentY && otherExtentY < choiceExtentY) {
            // right side of other overlaps
            yCorrection = -(otherExtentY - +choice.yPos);
          }

          if (xCorrection !== 0 && yCorrection !== 0) {
            // overlap exists
            this.draggedOverlapMap.set(other, { xCorrection, yCorrection });
          }
        }
      }
      return this.draggedOverlapMap.size;
    },
    handleItemOverlap(choice, category) {
      this.draggedOverlapMapReactivityFlag = !(this.draggedOverlapMapReactivityFlag);

      if (this.decision.overlapItemDropDisplayWarning && this.draggedOverlapMap.size > 0) {
        // eslint-disable-next-line no-alert
        alert(this.decision.overlapItemDropWarningMessage || 'Items cannot overlap.');
      }

      if (this.decision.overlapItemDropCancel && this.draggedOverlapMap.size > 0) {
        // revert drop
        choice.xPos = this.startXPos;
        choice.yPos = this.startYPos;
        this.draggedOverlapMap.clear();
        return false; // *** EARLY RETURN ************************************************
      } else if (this.decision.overlapItemDropAttemptCorrection) {
        for (let i = 0; i < this.decision.choices.length; ++i) {
          const other = this.decision.choices[i];
          // eslint-disable-next-line no-continue
          if (choice === other || !(this.draggedOverlapMap.has(other))) continue;

          const correction = this.draggedOverlapMap.get(other);
          const otherAspect = (this.aspectMap.has(other) ? this.aspectMap.get(other) : (9 / 16));
          const otherWidth = (+other.scale);
          const otherHeight = (+other.scale * +otherAspect);

          // apply correction
          if (correction.xCorrection > correction.yCorrection) {
            other.yPos += correction.yCorrection;
            if (other.yPos < category.yPos || (other.yPos > (category.yPos + category.height - otherHeight))) {
              other.xPos += correction.xCorrection;
            }
          } else {
            other.xPos += correction.xCorrection;
            if (other.xPos < category.xPos || (other.xPos > (category.xPos + category.width - otherWidth))) {
              other.yPos += correction.yCorrection;
            }
          }

          // category bounds restriction
          other.xPos = Math.max(other.xPos, category.xPos);
          other.yPos = Math.max(other.yPos, category.yPos);
          other.xPos = Math.min(other.xPos, (category.xPos + category.width - otherWidth));
          other.yPos = Math.min(other.yPos, (category.yPos + category.height - otherHeight));
        }
      }
      this.draggedOverlapMap.clear();
      return true;
    },
    computeChoices() {
      this.choices.length = 0; // clear
      this.decision.choices.forEach((choice) => {
        const copy = Object.assign(
          {},
          choice
        );
        copy.category = {};
        Object.assign(copy.category, this.computeCategoryForChoice(choice));
        this.choices.push(copy);
      });
    },
    onDragChoiceStart(settings) {
      this.draggedChoice = settings;
      this.startXPos = this.draggedChoice.xPos;
      this.startYPos = this.draggedChoice.yPos;
    },
    onDragChoiceMove(settings) {
      this.hoveredCategory = this.computeCategoryForChoice(settings);
      this.computeItemOverlap(settings);
    },
    onDragChoiceEnd(settings) {
      this.hoveredCategory = null;
      const choiceCategory = this.computeCategoryForChoice(settings);
      if (choiceCategory == null) {
        settings.xPos = this.startXPos;
        settings.yPos = this.startYPos;
      } else if (this.handleItemOverlap(settings, choiceCategory)) {
        this.computeChoices();
      }
    },
    onLoadedImg(ev, choice) {
      if (ev && ev.target && ev.target.naturalHeight && ev.target.naturalWidth) {
        this.aspectMap.set(choice, +(ev.target.naturalHeight / ev.target.naturalWidth));
      }
    },
    isFakeLandscape() {
      const { clientWidth, clientHeight } = document.documentElement;
      return !(this.disableForcedLandscape || isInIframe()) && clientWidth < 767 && clientHeight > clientWidth;
    },
    handleResize() {
      if (this.$refs.dragRoot) {
        this.scaleFactor =
          (this.isFakeLandscape()
            ? this.$refs.dragRoot.getBoundingClientRect().height
            : this.$refs.dragRoot.getBoundingClientRect().width
          ) / this.$refs.dragRoot.offsetWidth;
      }
    },
  },
  beforeCreate() {
    this.decision.choices.forEach((choice) => {
      if (choice.xPos == null) {
        choice.xPos = 0;
      }
      if (choice.yPos == null) {
        choice.yPos = 0;
      }
      if (choice.scale == null) {
        choice.scale = 200;
      }
    });
  },
  created() {
    this.decision.choices.forEach((choice) => {
      if (choice.xPos == null) {
        choice.xPos = 0;
      }
      if (choice.yPos == null) {
        choice.yPos = 0;
      }
      if (choice.scale == null) {
        choice.scale = 200;
      }
    });
    this.computeChoices();
  },
  async mounted() {
    window.addEventListener('orientationchange', this.handleResize);
    window.addEventListener('resize', this.handleResize);
    this.handleResize();
    this.stopSounds();
    if (!(this.decision.hideTitle) && this.decision.titleVoiceLine) {
      if (this.decision.description && this.decision.descriptionVoiceLine) {
        await Vue.nextTick();
        this.playVoice({ line: this.decision.titleVoiceLine, next: this.decision.descriptionVoiceLine });
      } else {
        await Vue.nextTick();
        this.playVoice({ line: this.decision.titleVoiceLine });
      }
    } else if (this.decision.description && this.decision.descriptionVoiceLine) {
      await Vue.nextTick();
      this.playVoice({ line: this.decision.descriptionVoiceLine });
    }
  },
  beforeDestroy() {
    window.removeEventListener('orientationchange', this.handleResize);
    window.removeEventListener('resize', this.handleResize);
  },
};
</script>

<style lang="scss" scoped>
.decision-free-drag-root {
  width: 1600px;
  height: 900px;
  position: absolute;
  top: 0;
  left: 0;
}

.decision-free-drag-text {
  pointer-events: none;
  -webkit-user-select: none; /* Safari */
  -moz-user-select: none; /* Firefox */
  -ms-user-select: none; /* IE10+/Edge */
  user-select: none; /* Standard */
  position: absolute;
  top: 8px;
  left: 16px;
  width: 38%;
}

.decision-free-drag-title {
  margin: 0 0 2px;
  width: 100%;
  text-align: center;
  font-weight: bolder;
}

.decision-free-drag-description {
  max-height: 24vh;
  overflow-y: auto;
  font-size: 1.25rem;
  line-height: 1.2;
  white-space: pre-wrap;
}

.decision-free-drag-submit {
  position: absolute;
  bottom: 20px;
  right: 55px;
  width: 165px;
  padding: 0;
  height: 75px;
  font-size: 40px;
  line-height: 40px;
  z-index: 1200;
}
</style>



// WEBPACK FOOTER //
// src/components/game/ui/DecisionFreeDrag.vue